// ============================================================
// CareRotaRoleAssignModal.jsx  (CareRota role model, 2026-07-14)
// Modal form to assign / change a user's CareRota module role.
// Clone of CareChargeRoleAssignModal — a two-value pair with NO tier
// select (CareRota has no scope-tier axis). Reuses the cp-role-modal
// styling.
//
//   - Radio: Rota Coordinator (full operational access) / Rota Manager
//     (operations + CareRota settings — the module-config tier).
//   - Inline error (from the parent's failed submit) keeps the modal open
//     so the admin can correct + retry.
// ============================================================

import React, { useEffect, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import Modal from './Modal';
import './CarePeopleRoleAssignModal.css';

const ROLE_OPTIONS = [
  { value: 'rota_coordinator', label: 'Rota Coordinator', help: 'Full CareRota operational access (rotas, shifts, assignments). No settings access.' },
  { value: 'rota_manager', label: 'Rota Manager', help: 'Everything the coordinator has + the CareRota settings page (read + write).' },
];

export default function CareRotaRoleAssignModal({
  isOpen,
  onClose,
  onSubmit,
  submitting = false,
  errorMessage = null,
  initialRole = 'rota_coordinator',
}) {
  const [role, setRole] = useState(initialRole);

  // Reset to the seeded value each time the modal opens.
  useEffect(() => {
    if (isOpen) setRole(initialRole);
  }, [isOpen, initialRole]);

  function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    onSubmit({ careRotaRole: role });
  }

  const selected = ROLE_OPTIONS.find((o) => o.value === role);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Assign CareRota role" closeDisabled={submitting}>
      <form className="cp-role-modal" onSubmit={handleSubmit}>
        <fieldset className="cp-role-modal__fieldset" disabled={submitting}>
          <legend className="cp-role-modal__legend">Role</legend>
          {ROLE_OPTIONS.map((o) => (
            <label className="cp-role-modal__radio" key={o.value}>
              <input
                type="radio"
                name="careRotaRole"
                value={o.value}
                checked={role === o.value}
                onChange={() => setRole(o.value)}
              />
              <span>{o.label}</span>
            </label>
          ))}
          {selected && <span className="cp-role-modal__help">{selected.help}</span>}
        </fieldset>

        {errorMessage && (
          <div className="cp-role-modal__error" role="alert">
            <AlertCircle size={14} />
            <span>{errorMessage}</span>
          </div>
        )}

        <div className="cp-role-modal__actions">
          <button type="button" className="cp-role-modal__cancel" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="cp-role-modal__submit" disabled={submitting}>
            {submitting && <Loader2 size={14} className="cp-role-modal__spinner" />}
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}
